import { API_URL, fetchWithHandling } from './api';

// ---------------------------------------------------------------------------
// Mail Intelligence API
//
// The caller's own mailbox enrolment and the weekly gate audit. Owner-only on
// the backend (mail_intel:mailbox:connect). Responses are the AppResponse
// envelope.
// ---------------------------------------------------------------------------

const BASE = `${API_URL}/api/mail-intel`;

const auth = (accessToken) => ({
  'Content-Type': 'application/json',
  Authorization: `Bearer ${accessToken}`,
});

export async function getMailbox(accessToken) {
  return fetchWithHandling(`${BASE}/mailbox`, {
    method: 'GET',
    headers: auth(accessToken),
  });
}

/** Unanswered audit samples; subject/preview are fetched live, never stored. */
export async function listAuditSamples(accessToken) {
  return fetchWithHandling(`${BASE}/audit`, {
    method: 'GET',
    headers: auth(accessToken),
  });
}

export async function answerAuditSample(accessToken, sampleId, projectMail) {
  return fetchWithHandling(`${BASE}/audit/${encodeURIComponent(sampleId)}`, {
    method: 'POST',
    headers: auth(accessToken),
    body: JSON.stringify({ project_mail: projectMail }),
  });
}

// ---------------------------------------------------------------------------
// Triage (Phase 2). Project-scoped by the project token; ranked for the
// caller's delivery role. Inbox and panel never call a model; the summary is
// one cached GLM call, fetched separately so the panel never waits on it.
// ---------------------------------------------------------------------------

/** { data: { role, decision: [...], attention: [...], other: [...] } } */
export async function getInbox(accessToken) {
  return fetchWithHandling(`${BASE}/inbox`, {
    method: 'GET',
    headers: auth(accessToken),
  });
}

export async function getThread(accessToken, threadId) {
  return fetchWithHandling(`${BASE}/threads/${encodeURIComponent(threadId)}`, {
    method: 'GET',
    headers: auth(accessToken),
  });
}

/** { data: { available, summary?, next_step?, cached? } } */
export async function getThreadSummary(accessToken, threadId) {
  return fetchWithHandling(
    `${BASE}/threads/${encodeURIComponent(threadId)}/summary`,
    { method: 'GET', headers: auth(accessToken) },
  );
}

// ---------------------------------------------------------------------------
// Decision owners: which delivery role makes each kind of decision on this
// project. Read by every member; saved only with mail_intel:decision_map:manage.
// ---------------------------------------------------------------------------

/** { data: { map, overrides, version, defaults, decision_types, roles, can_edit } } */
export async function getDecisionMap(accessToken) {
  return fetchWithHandling(`${BASE}/decision-map`, {
    method: 'GET',
    headers: auth(accessToken),
  });
}

/**
 * Replace the project's overrides. `expectedVersion` is the version the
 * editor loaded; if someone saved in between, the backend refuses (message
 * mentions "reload") instead of overwriting their change.
 */
export async function saveDecisionMap(accessToken, overrides, expectedVersion) {
  return fetchWithHandling(`${BASE}/decision-map`, {
    method: 'PUT',
    headers: auth(accessToken),
    body: JSON.stringify({ overrides, expected_version: expectedVersion }),
  });
}

// ---------------------------------------------------------------------------
// Reply drafting (Phase 3). One explicit request per click; the backend makes
// one or two GLM calls (the second only to repair rule failures), so allow
// ~30 s. Nothing is ever sent: Outlook save creates a draft in the user's
// own Drafts folder.
// ---------------------------------------------------------------------------

/** { data: { id, variants: [{label, subject, body, open_decisions, trace, warnings}], recipients, repaired } } */
export async function createDraft(
  accessToken,
  threadId,
  targetMessageId,
  rawInput,
) {
  return fetchWithHandling(
    `${BASE}/threads/${encodeURIComponent(threadId)}/drafts`,
    {
      method: 'POST',
      headers: auth(accessToken),
      body: JSON.stringify({
        target_message_id: targetMessageId,
        raw_input: rawInput,
      }),
    },
  );
}

/**
 * A reply offered without the user's points. Written once per target mail on
 * the backend, so asking again for the same mail returns the same one.
 */
export async function suggestReply(accessToken, threadId, targetMessageId) {
  return fetchWithHandling(
    `${BASE}/threads/${encodeURIComponent(threadId)}/drafts`,
    {
      method: 'POST',
      headers: auth(accessToken),
      body: JSON.stringify({
        target_message_id: targetMessageId,
        suggest: true,
      }),
    },
  );
}

/**
 * Send the reviewed reply from the user's own mailbox. `fields`: { variant,
 * body, to, cc, subject, acknowledged }; to / cc / subject are left out to
 * keep what Outlook sets for a reply-all. Errors carry a code: BRACKETS_LEFT,
 * RECIPIENTS_CHANGED (details.warnings, details.joined), RECONNECT_REQUIRED,
 * ALREADY_SENT, SENDING_OFF, SEND_FAILED.
 */
export async function sendDraft(accessToken, draftId, fields) {
  return fetchWithHandling(
    `${BASE}/drafts/${encodeURIComponent(draftId)}/send`,
    {
      method: 'POST',
      headers: auth(accessToken),
      body: JSON.stringify(fields),
    },
  );
}

/** { data: { outlook_draft_id, web_link, brackets_left } }; 409 = reconnect. */
export async function saveDraftToOutlook(accessToken, draftId, variant, body) {
  return fetchWithHandling(
    `${BASE}/drafts/${encodeURIComponent(draftId)}/outlook`,
    {
      method: 'POST',
      headers: auth(accessToken),
      body: JSON.stringify({ variant, body }),
    },
  );
}

// ---------------------------------------------------------------------------
// Mail settings (Phase 3 admin): client communication profiles (PM) and
// guideline modules (admin). List responses carry can_edit / can_manage as
// UI hints; the backend enforces permissions on every write.
// ---------------------------------------------------------------------------

export async function listClientProfiles(accessToken) {
  return fetchWithHandling(`${BASE}/client-profiles`, {
    method: 'GET',
    headers: auth(accessToken),
  });
}

export async function saveClientProfile(accessToken, clientKey, fields) {
  return fetchWithHandling(
    `${BASE}/client-profiles/${encodeURIComponent(clientKey)}`,
    { method: 'PUT', headers: auth(accessToken), body: JSON.stringify(fields) },
  );
}

/** FR-GM-02: profile suggested from the caller's own sent mail; not saved. */
export async function suggestClientProfile(accessToken, clientKey) {
  return fetchWithHandling(
    `${BASE}/client-profiles/${encodeURIComponent(clientKey)}/suggestion`,
    { method: 'GET', headers: auth(accessToken) },
  );
}

export async function listGuidelines(accessToken) {
  return fetchWithHandling(`${BASE}/guidelines`, {
    method: 'GET',
    headers: auth(accessToken),
  });
}

/** A new draft version; it is used only after activateGuideline. */
export async function createGuidelineDraft(
  accessToken,
  dimension,
  value,
  fragment,
) {
  return fetchWithHandling(`${BASE}/guidelines`, {
    method: 'POST',
    headers: auth(accessToken),
    body: JSON.stringify({ dimension, value, fragment }),
  });
}

export async function activateGuideline(accessToken, ref) {
  return fetchWithHandling(`${BASE}/guidelines/activate`, {
    method: 'POST',
    headers: auth(accessToken),
    body: JSON.stringify({ ref }),
  });
}

/** Release gate: runs the draft eval set with this draft (about a minute). */
export async function evaluateGuideline(accessToken, ref) {
  return fetchWithHandling(`${BASE}/guidelines/eval`, {
    method: 'POST',
    headers: auth(accessToken),
    body: JSON.stringify({ ref }),
  });
}

export async function retireGuideline(accessToken, ref) {
  return fetchWithHandling(`${BASE}/guidelines/retire`, {
    method: 'POST',
    headers: auth(accessToken),
    body: JSON.stringify({ ref }),
  });
}

// ---------------------------------------------------------------------------
// Settings without a deploy: triage settings (role weights, bands, hours,
// stage switches, tracked types) and relevance gate rules. Config.View to
// read, Config.Manage to change; can_edit is a UI hint only.
// ---------------------------------------------------------------------------

export async function getMailConfig(accessToken) {
  return fetchWithHandling(`${BASE}/config`, {
    method: 'GET',
    headers: auth(accessToken),
  });
}

/** Overrides only (changes from the defaults); stale versions get a 409. */
export async function saveMailConfig(accessToken, overrides, expectedVersion) {
  return fetchWithHandling(`${BASE}/config`, {
    method: 'PUT',
    headers: auth(accessToken),
    body: JSON.stringify({ overrides, expected_version: expectedVersion }),
  });
}

export async function listGateRules(accessToken) {
  return fetchWithHandling(`${BASE}/rules`, {
    method: 'GET',
    headers: auth(accessToken),
  });
}

export async function addGateRule(accessToken, rule) {
  return fetchWithHandling(`${BASE}/rules`, {
    method: 'POST',
    headers: auth(accessToken),
    body: JSON.stringify(rule),
  });
}

export async function setGateRuleActive(accessToken, ruleId, isActive) {
  return fetchWithHandling(
    `${BASE}/rules/${encodeURIComponent(ruleId)}/active`,
    {
      method: 'POST',
      headers: auth(accessToken),
      body: JSON.stringify({ is_active: isActive }),
    },
  );
}

/** Model usage per stage, per day and per user (Config.View). */
export async function getMailUsage(accessToken, days = 7) {
  return fetchWithHandling(`${BASE}/usage?days=${encodeURIComponent(days)}`, {
    method: 'GET',
    headers: auth(accessToken),
  });
}
